import { useCallback, useMemo } from "react";

import { useWorkspace } from "#app/lib/data/workspace-context";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";

/**
 * Recently-opened entities — projects — mirrored per workspace in
 * localStorage. Two readers: global search (the launcher's "Results" section,
 * and floating recent matches to the top of a typed search) and the My Work
 * Recent widget. Dashboards keep their own recents
 * (`components/dashboards/use-recent-dashboards.ts`), dated by the server's
 * `viewerLastViewedAt`, so the Recent widget merges the two at read time.
 *
 * Entries are display SNAPSHOTS ({id, name, icon…}), not bare ids, so global
 * search renders with zero fetching — projects live in no workspace-wide map.
 * A snapshot can go stale (a rename after the last open); the next open
 * refreshes it, and a row pointing at a slightly-old name is harmless because
 * selecting it navigates by id.
 *
 * Built on `usePersistedState`, so the writer (a detail overlay) and every
 * reader see one list reactively — the key is workspace-scoped because the
 * ids are only meaningful inside the workspace that holds them.
 */
export type RecentItemKind = "issue";

export type RecentItem = {
  kind: RecentItemKind;
  id: string;
  name: string;
  /** The project TYPE's icon — a project's display identity. */
  iconName?: string | null;
  iconColor?: string | null;
  workflowId?: string | null;
  /** Epoch ms of the last open — recency expiry keys off it. */
  visitedAt: number;
};

const CAP = 20;

/** How long an entry stays a "recent": past this it stops showing and is
 *  dropped from storage on the next write. */
export const RECENT_ITEM_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function sameItem(a: { kind: RecentItemKind; id: string }, b: { kind: RecentItemKind; id: string }) {
  return a.kind === b.kind && a.id === b.id;
}

export function useRecentItems(): {
  /** Most-recently-opened entities inside the recency window, newest first. */
  items: RecentItem[];
  recordVisit: (item: Omit<RecentItem, "visitedAt">) => void;
  /** Drops one entity — a Project moved to the trash is no longer a place
   *  to go back to. */
  forgetVisit: (item: { kind: RecentItemKind; id: string }) => void;
} {
  const { activeWorkspace } = useWorkspace();
  const { state, update } = usePersistedState(
    `recent-items:${activeWorkspace?.workspaceId ?? ""}`,
    { items: [] as RecentItem[] },
  );

  // The window is re-applied on read: storage only prunes on write, and a
  // session can outlive the window.
  const items = useMemo(() => {
    const cutoff = Date.now() - RECENT_ITEM_MAX_AGE_MS;
    return state.items.filter((item) => item.visitedAt >= cutoff);
  }, [state.items]);

  const recordVisit = useCallback(
    (item: Omit<RecentItem, "visitedAt">) => {
      if (!item.id) return;
      const cutoff = Date.now() - RECENT_ITEM_MAX_AGE_MS;
      update((current) => ({
        items: [
          { ...item, visitedAt: Date.now() },
          ...current.items.filter(
            (existing) => !sameItem(existing, item) && existing.visitedAt >= cutoff,
          ),
        ].slice(0, CAP),
      }));
    },
    [update],
  );

  const forgetVisit = useCallback(
    (item: { kind: RecentItemKind; id: string }) => {
      update((current) =>
        current.items.some((existing) => sameItem(existing, item))
          ? { items: current.items.filter((existing) => !sameItem(existing, item)) }
          : current,
      );
    },
    [update],
  );

  return { items, recordVisit, forgetVisit };
}
